import type { DailyInteractionRequest } from '../domain/daily'
import type { DailyPlan } from '../domain/dailyEngine'

const DATABASE_NAME = 'pmm-encrypted-device-vault-v1'
const STORE_NAME = 'records'
const DATABASE_VERSION = 1
const STATE_SCHEMA = 'pmm-offline-state-1'

interface EncryptedRecord {
  id: string
  schemaVersion: 'pmm-encrypted-record-1'
  iv: string
  ciphertext: string
}

interface OfflineQueueItem {
  request: DailyInteractionRequest
  queuedAt: string
  attempts: number
  state: 'pending' | 'conflict'
  lastError?: string
}

interface OfflineState {
  schemaVersion: typeof STATE_SCHEMA
  todayPlan: DailyPlan | null
  queuedInteractions: OfflineQueueItem[]
  updatedAt: string
}

const emptyState = (): OfflineState => ({
  schemaVersion: STATE_SCHEMA,
  todayPlan: null,
  queuedInteractions: [],
  updatedAt: new Date().toISOString(),
})

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}

async function ownerKey(uid: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`pmm-owner:${uid}`))
  return bytesToBase64(new Uint8Array(digest)).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '').slice(0, 24)
}

function openDatabase(): Promise<IDBDatabase> {
  if (!('indexedDB' in globalThis)) return Promise.reject(new Error('offline_storage_unavailable'))
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME, { keyPath: 'id' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('offline_database_open_failed'))
    request.onblocked = () => reject(new Error('offline_database_blocked'))
  })
}

async function getRecord<T>(id: string): Promise<T | undefined> {
  const database = await openDatabase()
  try {
    return await new Promise((resolve, reject) => {
      const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(id)
      request.onsuccess = () => resolve(request.result as T | undefined)
      request.onerror = () => reject(request.error ?? new Error('offline_record_read_failed'))
    })
  } finally {
    database.close()
  }
}

async function putRecord(value: unknown): Promise<void> {
  const database = await openDatabase()
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite')
      transaction.objectStore(STORE_NAME).put(value)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error ?? new Error('offline_record_write_failed'))
      transaction.onabort = () => reject(transaction.error ?? new Error('offline_record_write_aborted'))
    })
  } finally {
    database.close()
  }
}

async function getOrCreateCryptoKey(owner: string): Promise<CryptoKey> {
  const id = `key:${owner}`
  const existing = await getRecord<{ id: string; key: CryptoKey }>(id)
  if (existing?.key) return existing.key
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'])
  await putRecord({ id, key, schemaVersion: 'pmm-device-key-1' })
  return key
}

async function readState(uid: string): Promise<OfflineState> {
  const owner = await ownerKey(uid)
  const record = await getRecord<EncryptedRecord>(`state:${owner}`)
  if (!record) return emptyState()
  if (record.schemaVersion !== 'pmm-encrypted-record-1') throw new Error('offline_schema_mismatch')
  const key = await getOrCreateCryptoKey(owner)
  try {
    const plaintext = await crypto.subtle.decrypt({
      name: 'AES-GCM',
      iv: base64ToBytes(record.iv),
      additionalData: new TextEncoder().encode(`pmm:${owner}:${STATE_SCHEMA}`),
    }, key, base64ToBytes(record.ciphertext))
    const parsed = JSON.parse(new TextDecoder().decode(plaintext)) as OfflineState
    if (parsed.schemaVersion !== STATE_SCHEMA || !Array.isArray(parsed.queuedInteractions)) throw new Error('offline_schema_mismatch')
    return parsed
  } catch {
    throw new Error('offline_vault_could_not_be_opened')
  }
}

async function writeState(uid: string, state: OfflineState): Promise<void> {
  const owner = await ownerKey(uid)
  const key = await getOrCreateCryptoKey(owner)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const plaintext = new TextEncoder().encode(JSON.stringify({ ...state, updatedAt: new Date().toISOString() }))
  const ciphertext = await crypto.subtle.encrypt({
    name: 'AES-GCM',
    iv,
    additionalData: new TextEncoder().encode(`pmm:${owner}:${STATE_SCHEMA}`),
  }, key, plaintext)
  await putRecord({
    id: `state:${owner}`,
    schemaVersion: 'pmm-encrypted-record-1',
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
  } satisfies EncryptedRecord)
}

export async function cacheOfflineToday(uid: string, plan: DailyPlan): Promise<void> {
  const state = await readState(uid)
  await writeState(uid, { ...state, todayPlan: plan })
}

export async function readOfflineToday(uid: string, localDate: string): Promise<DailyPlan | null> {
  const state = await readState(uid)
  return state.todayPlan?.localDate === localDate ? { ...state.todayPlan, deliveryState: 'offline_cache' } : null
}

export async function queueOfflineInteraction(uid: string, request: DailyInteractionRequest): Promise<number> {
  const state = await readState(uid)
  if (!state.queuedInteractions.some((item) => item.request.clientMutationId === request.clientMutationId)) {
    state.queuedInteractions.push({ request, queuedAt: new Date().toISOString(), attempts: 0, state: 'pending' })
  }
  if (state.queuedInteractions.length > 100) throw new Error('offline_queue_capacity_reached')
  await writeState(uid, state)
  return state.queuedInteractions.length
}

export async function readOfflineQueue(uid: string): Promise<OfflineQueueItem[]> {
  return (await readState(uid)).queuedInteractions
}

export async function replaceOfflineQueue(uid: string, queuedInteractions: OfflineQueueItem[]): Promise<void> {
  const state = await readState(uid)
  await writeState(uid, { ...state, queuedInteractions })
}

export async function clearEncryptedDeviceVault(): Promise<void> {
  if (!('indexedDB' in globalThis)) return
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error ?? new Error('offline_database_delete_failed'))
    request.onblocked = () => reject(new Error('offline_database_delete_blocked'))
  })
}

export async function clearAllPmmSensitiveDeviceState(): Promise<void> {
  await clearEncryptedDeviceVault().catch(() => undefined)
  if (typeof window !== 'undefined') {
    for (const storage of [window.sessionStorage, window.localStorage]) {
      for (let index = storage.length - 1; index >= 0; index -= 1) {
        const key = storage.key(index)
        if (key?.startsWith('pmm:')) storage.removeItem(key)
      }
    }
  }
  if ('caches' in globalThis) {
    const keys = await caches.keys()
    await Promise.all(keys.filter((key) => key.startsWith('pmm-user-')).map((key) => caches.delete(key)))
  }
  const registration = typeof navigator !== 'undefined' && 'serviceWorker' in navigator
    ? await navigator.serviceWorker.getRegistration('/').catch(() => undefined)
    : undefined
  registration?.active?.postMessage({ type: 'PMM_CLEAR_SENSITIVE_STATE' })
}

export type { OfflineQueueItem }
